import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface Deal {
  id: number;
  lead_id: number;
  title: string;
  lead_name: string;
  property_title: string | null;
  value: string;
  stage: string;
  probability: number;
  close_date: string | null;
  rm_name: string;
}

interface ListResponse {
  data: Deal[];
  total: number;
  page: number;
  limit: number;
}

const STAGE_OPTIONS = ["qualified", "proposal", "negotiation", "won", "lost"];

const STAGE_BADGE: Record<string, string> = {
  qualified: "bg-secondary-100 text-secondary-900",
  proposal: "bg-primary-100 text-primary-900",
  negotiation: "bg-accent-100 text-accent-900",
  won: "bg-primary-600 text-background-50",
  lost: "bg-background-200 text-foreground-700",
};

function fmtMoney(v: string | number | null): string {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `₹${n.toLocaleString("en-IN")}` : "—";
}

export default function AdminDeals() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Deal | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (stage) params.set("stage", stage);
    params.set("page", String(page));
    params.set("limit", "15");
    try {
      const res = await api.get<ListResponse>(`/deals?${params.toString()}`);
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load deals");
    } finally {
      setLoading(false);
    }
  }, [stage, page]);

  useEffect(() => { load(); }, [load]);

  const pageCount = data ? Math.max(Math.ceil(data.total / data.limit), 1) : 1;
  const pipelineCounts = STAGE_OPTIONS.map((s) => ({
    stage: s,
    count: data?.data.filter((d) => d.stage === s).length ?? 0,
  }));

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="font-heading text-xl text-foreground-950">Deals Pipeline</h2>
          <p className="text-sm text-foreground-600">Track JDA, lease and equity deals through to closure.</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700"
        >
          <i className="ri-add-line"></i> Add Deal
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {pipelineCounts.map((p) => (
          <div key={p.stage} className="rounded-2xl bg-background-100 border border-background-200 p-4">
            <p className="text-[11px] uppercase tracking-wide text-foreground-600">{p.stage}</p>
            <p className="font-heading text-2xl text-foreground-950 mt-1">{p.count}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={stage}
          onChange={(e) => { setStage(e.target.value); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Stages</option>
          {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {stage && (
          <button onClick={() => { setStage(""); setPage(1); }} className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-700">
            <i className="ri-close-circle-line"></i> Clear
          </button>
        )}
      </div>

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3">Deal</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Close</th>
                <th className="px-4 py-3">RM</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-foreground-600">
                  <i className="ri-loader-4-line animate-spin"></i> Loading...
                </td></tr>
              )}
              {!loading && data?.data.map((deal) => (
                <tr key={deal.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground-950">{deal.title}</p>
                      <p className="text-xs text-foreground-600">{deal.lead_name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-700 max-w-[160px] truncate">{deal.property_title || "—"}</td>
                  <td className="px-4 py-3 font-semibold text-foreground-950">{fmtMoney(deal.value)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STAGE_BADGE[deal.stage] || "bg-background-200 text-foreground-700"}`}>
                      {deal.stage}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">
                    {deal.close_date ? new Date(deal.close_date + "T00:00:00").toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{deal.rm_name || "Unassigned"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setDetail(deal)}
                        className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100"
                        aria-label="View"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data?.data.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-foreground-600">No deals found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-background-100">
          <p className="text-xs text-foreground-600">Total: {data?.total ?? 0}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <span className="px-3 text-sm text-foreground-700">{page} / {pageCount}</span>
            <button onClick={() => setPage((p) => Math.min(p + 1, pageCount))} disabled={page >= pageCount} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>

      {detail && <DealDetail deal={detail} onClose={() => setDetail(null)} onChanged={load} />}
      {createOpen && <CreateDeal onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
}

function DealDetail({ deal, onClose, onChanged }: { deal: Deal; onClose: () => void; onChanged: () => void }) {
  const [stage, setStage] = useState("");
  const [saving, setSaving] = useState(false);

  async function saveStage() {
    if (!stage) return;
    setSaving(true);
    try {
      await api.put(`/deals/${deal.id}`, { stage });
      setStage("");
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-3xl bg-background-50 border border-background-200 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100 sticky top-0 bg-background-50">
          <div>
            <h3 className="font-heading text-lg text-foreground-950">{deal.title}</h3>
            <p className="text-xs text-foreground-600">{deal.lead_name}</p>
          </div>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Project" value={deal.property_title || "—"} />
            <Info label="Value" value={fmtMoney(deal.value)} />
            <Info label="Stage" value={deal.stage} />
            <Info label="Close Date" value={deal.close_date ? new Date(deal.close_date + "T00:00:00").toLocaleDateString() : "—"} />
            <Info label="RM" value={deal.rm_name || "Unassigned"} />
            <Info label="Probability" value={`${deal.probability}%`} />
          </div>

          <div className="rounded-2xl bg-background-100 p-4">
            <p className="text-xs font-semibold text-foreground-800 mb-2">Update Stage</p>
            <div className="flex gap-2">
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="flex-1 rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="">Select stage...</option>
                {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={saveStage} disabled={saving} className="inline-flex items-center gap-1 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-4 py-2 hover:bg-primary-700 disabled:opacity-60">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-background-100 p-3">
      <p className="text-[10px] uppercase tracking-wide text-foreground-600">{label}</p>
      <p className="text-sm font-semibold text-foreground-950 capitalize">{value || "—"}</p>
    </div>
  );
}

function CreateDeal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [leads, setLeads] = useState<any[]>([]);
  const [form, setForm] = useState({ lead_id: "", title: "", value: "", close_date: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<any>(`/leads?limit=200`).then((res: any) => setLeads(res?.data ?? [])).catch(() => setLeads([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/deals", {
        lead_id: Number(form.lead_id),
        title: form.title,
        value: form.value ? Number(form.value) : 0,
        stage: "qualified",
        close_date: form.close_date || null,
      });
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-background-50 border border-background-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
          <h3 className="font-heading text-lg text-foreground-950">Add Deal</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Deal Title *</label>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Highway frontage retail lease" className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Lead *</label>
            <select required value={form.lead_id} onChange={(e) => setForm((f) => ({ ...f, lead_id: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
              <option value="">Select lead...</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Deal Value (₹)</label>
            <input type="number" min="0" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Close Date</label>
            <input type="date" value={form.close_date} onChange={(e) => setForm((f) => ({ ...f, close_date: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Create Deal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}